import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
     Container,
     Paper,
     Stack,
     Title,
     Text,
     Button,
     Group,
     Loader,
     ThemeIcon,
} from "@mantine/core";
import {
     IconCheck,
     IconAlertCircle,
     IconArrowRight,
} from "@tabler/icons-react";
import { Endpoints } from "../../lib/endpoints";
import { PerformRequest } from "../../lib/usePerformRequest";
import { HttpCodes } from "../../lib/data";

export default function PaystackDirectDebitCallback() {
     const navigate = useNavigate();
     const [searchParams] = useSearchParams();

     const reference = searchParams.get("reference") || searchParams.get("ref");
     const status = searchParams.get("status");

     const isSuccess = status === "success" || status === "approved";

     useEffect(() => {
          // Notify backend to confirm direct debit mandate when Paystack callback returns
          const confirmMandate = async () => {
               try {
                    if (!status) return;
                    const confirmResp = await PerformRequest({
                         method: Endpoints.GetConfirmDirectDebitMandate()
                              .method,
                         route: Endpoints.GetConfirmDirectDebitMandate().route,
                         data: {
                              reference,
                              status,
                         },
                    });

                    // Best-effort: verify mandate status immediately so UI can reflect created vs active
                    if (
                         confirmResp?.status === HttpCodes.Success &&
                         reference
                    ) {
                         await PerformRequest({
                              method: Endpoints.GetVerifyDirectDebitMandate()
                                   .method,
                              route: Endpoints.GetVerifyDirectDebitMandate()
                                   .route,
                              data: {
                                   reference,
                              },
                         });
                    }
               } catch (e) {
                    // Silently ignore errors - UI messaging stays generic
                    console.error("Failed to confirm direct debit mandate", e);
               }
          };

          confirmMandate();

          const timeout = setTimeout(() => {
               navigate("/dashboard/profile", { replace: true });
          }, 5000);

          return () => clearTimeout(timeout);
     }, [navigate, reference, status]);

     return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
               <Container size="sm" py="xl">
                    <Paper
                         radius="md"
                         p="xl"
                         withBorder
                         style={{
                              background: "white",
                              borderColor: "#e5e7eb",
                         }}
                    >
                         <Stack gap="lg" align="center">
                              <ThemeIcon
                                   size="xl"
                                   radius="xl"
                                   color={isSuccess ? "green" : "yellow"}
                                   variant="light"
                              >
                                   {isSuccess ? (
                                        <IconCheck size={28} />
                                   ) : (
                                        <IconAlertCircle size={28} />
                                   )}
                              </ThemeIcon>

                              <Stack gap={4} align="center" ta="center">
                                   <Title order={3}>
                                        {isSuccess
                                             ? "Bank Mandate in Progress"
                                             : "Processing Bank Mandate"}
                                   </Title>
                                   <Text size="sm" c="dimmed">
                                        We&apos;ve received a response from
                                        Paystack for your direct debit setup.
                                        Your mandate status will be updated
                                        shortly.
                                   </Text>
                                   {reference && (
                                        <Text size="xs" c="dimmed">
                                             Reference:{" "}
                                             <strong>{reference}</strong>
                                        </Text>
                                   )}
                              </Stack>

                              <Group gap="sm">
                                   <Loader size="sm" />
                                   <Text size="xs" c="dimmed">
                                        Redirecting you to your profile in a few
                                        seconds...
                                   </Text>
                              </Group>

                              <Button
                                   size="sm"
                                   rightSection={<IconArrowRight size={16} />}
                                   onClick={() =>
                                        navigate("/dashboard/profile", {
                                             replace: true,
                                        })
                                   }
                                   style={{
                                        background:
                                             "linear-gradient(135deg, #699269 0%, #558B2F 100%)",
                                   }}
                              >
                                   Go to Profile
                              </Button>
                         </Stack>
                    </Paper>
               </Container>
          </div>
     );
}
